import { useState } from "react";
import { toast } from "sonner";
import { Mail, ArrowRight, MapPin } from "lucide-react";

const INTERESTS = [
    "Private Credit",
    "Enterprise",
    "Mortgage",
    "Venture",
    "Other",
];

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        interest: "Private Credit",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const update = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please complete the required fields.");
            return;
        }
        setSubmitting(true);

        const formData = new FormData();
        formData.append("access_key", "94838736-0dc2-48dc-b410-ab1302f33c43");
        formData.append("name", form.name);
        formData.append("email", form.email);
        formData.append("message", form.message);
        formData.append("interest", form.interest);
        formData.append("subject", `New Enquiry from ${form.name} — ${form.interest}`);

        try {
            const response = await fetch("https://api.web3forms.com/submit", {
                method: "POST",
                body: formData,
            });
            const data = await response.json();
            if (data.success) {
                toast.success("Enquiry received. A QuickFin advisor will respond within one business day.");
                setForm({ name: "", email: "", interest: "Private Credit", message: "" });
            } else {
                toast.error(data.message || "Something went wrong. Please try again.");
            }
        } catch {
            toast.error("Failed to send. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative py-20 md:py-28 bg-cream-warm border-t border-cream-deep"
        >
            <div className="max-w-[1280px] mx-auto px-6 md:px-10">
                {/* Header */}
                <div className="grid md:grid-cols-12 gap-8 mb-12">
                    <div className="md:col-span-7">
                        <div className="flex items-center gap-3 mb-5">
                            <span className="w-8 h-px bg-gold" />
                            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
                                Begin a Conversation
                            </span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl leading-[1] tracking-tight font-medium text-ink">
                            Ready for the<br />
                            <em className="italic text-gold font-normal">next</em> chapter?
                        </h2>
                    </div>
                    <div className="md:col-span-4 md:col-start-9 flex flex-col justify-end gap-5">
                        <p className="text-ink-muted font-light leading-relaxed text-lg">
                            Share your context below. An advisor responds within one business day — privately, with no obligation.
                        </p>
                        <a
                            href="mailto:info@thequickfin.com"
                            data-testid="contact-info-email"
                            className="inline-flex items-center gap-3 group self-start"
                        >
                            <div className="w-8 h-8 border border-gold/50 bg-gold/10 flex items-center justify-center shrink-0 group-hover:bg-gold/20 transition-colors">
                                <Mail size={13} className="text-gold" />
                            </div>
                            <span className="font-mono text-[12px] tracking-[0.06em] text-ink group-hover:text-gold transition-colors border-b border-ink/20 group-hover:border-gold pb-px">
                                info@thequickfin.com
                            </span>
                        </a>
                        <div
                            data-testid="contact-info-address"
                            className="inline-flex items-start gap-3 self-start"
                        >
                            <div className="w-8 h-8 border border-forest/50 bg-forest/10 flex items-center justify-center shrink-0 mt-0.5">
                                <MapPin size={13} className="text-forest" />
                            </div>
                            <span className="font-mono text-[12px] tracking-[0.06em] text-ink-muted leading-relaxed">
                                2709, 27th Floor, Neo Prime Tower<br />
                                Business Bay, Dubai, UAE
                            </span>
                        </div>
                    </div>
                </div>

                {/* Full-width form */}
                <div className="bg-white border border-cream-deep p-7 md:p-10">
                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                        <div className="grid md:grid-cols-2 gap-5">
                            <Field
                                label="Full Name"
                                testid="contact-name"
                                value={form.name}
                                onChange={update("name")}
                                required
                            />
                            <Field
                                label="Email Address"
                                type="email"
                                testid="contact-email"
                                value={form.email}
                                onChange={update("email")}
                                required
                            />
                        </div>

                        <div>
                            <label className="font-mono text-[11px] uppercase tracking-[0.3em] text-ink-muted mb-2.5 block">
                                Mandate of Interest
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {INTERESTS.map((opt) => (
                                    <button
                                        key={opt}
                                        type="button"
                                        data-testid={`interest-${opt.toLowerCase().replace(/\s/g, "-")}`}
                                        onClick={() => setForm((f) => ({ ...f, interest: opt }))}
                                        className={`px-3.5 py-2 font-mono text-[11px] uppercase tracking-[0.2em] border transition-all ${
                                            form.interest === opt
                                                ? "bg-ink text-cream border-ink"
                                                : "bg-white border-cream-deep text-ink-muted hover:border-gold hover:text-gold"
                                        }`}
                                    >
                                        {opt}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="font-mono text-[11px] uppercase tracking-[0.3em] text-ink-muted mb-2.5 block">
                                Your Context <span className="text-coral">*</span>
                            </label>
                            <textarea
                                data-testid="contact-message"
                                required
                                value={form.message}
                                onChange={update("message")}
                                rows={4}
                                placeholder="Tell us what you are working towards..."
                                className="w-full bg-cream-warm/40 border border-cream-deep px-4 py-3 text-ink placeholder:text-ink-muted/60 focus:border-gold focus:bg-white focus:outline-none font-light resize-none transition-all text-base"
                            />
                        </div>

                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="contact-submit"
                                className="group w-full md:w-auto inline-flex items-center justify-center gap-3 bg-gold text-white px-7 py-4 font-mono text-[12px] uppercase tracking-[0.25em] hover:bg-forest transition-colors disabled:opacity-50"
                            >
                                {submitting ? "Sending..." : "Send Enquiry"}
                                <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                            </button>
                            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-muted">
                                Bound by QuickFin&apos;s discretion clause · Encrypted
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
}

function Field({ label, type = "text", value, onChange, required, testid }) {
    return (
        <div>
            <label className="font-mono text-[11px] uppercase tracking-[0.3em] text-ink-muted mb-2.5 block">
                {label} {required && <span className="text-coral">*</span>}
            </label>
            <input
                data-testid={testid}
                type={type}
                value={value}
                onChange={onChange}
                required={required}
                className="w-full bg-cream-warm/40 border border-cream-deep px-4 py-3 text-ink placeholder:text-ink-muted/60 focus:border-gold focus:bg-white focus:outline-none font-light transition-all text-base"
            />
        </div>
    );
}
